import EditIcon from "@mui/icons-material/Edit"
import { Box, Button, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import defaultAvatar from "../assets/default_avatar.png"
import { selectMe } from "../features/me/meSlice"
import { useAuthStatus } from "../hooks/useAuthStatus"
import UsLayout from "./UsLayout"
import UserProfileForm from "./UserProfileForm"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const UserProfilePage: FC = () => {
  const { imageUrl } = useAuthStatus()
  const { user } = useSelector(selectMe)
  const { t } = useTranslation()

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ")

  return (
    <UsLayout selectedMenuItemLabelKey="profile">
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          {user?.email && (
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {user.email}
            </Typography>
          )}
          <Typography
            variant="h4"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            {t("profile")}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column-reverse", md: "row" }}
          spacing={{ xs: 3, md: 4 }}
          alignItems="flex-start"
        >
          {/* Form column */}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <UserProfileForm />
          </Box>

          {/* Identity card */}
          <Box
            sx={{
              ...cardSx,
              p: 3,
              width: { xs: "100%", md: 280 },
              flexShrink: 0,
              alignSelf: { xs: "stretch", md: "flex-start" },
              position: { md: "sticky" },
              top: { md: 24 },
            }}
          >
            <Stack direction="column" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 6px 18px rgba(35, 66, 133, 0.08)",
                }}
              >
                <Box
                  component="img"
                  alt="User"
                  src={imageUrl || defaultAvatar}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
              {fullName && (
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mt: 0.5 }}
                  noWrap
                >
                  {fullName}
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                disabled
                startIcon={<EditIcon fontSize="small" />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                Edit
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </UsLayout>
  )
}

export default UserProfilePage
